import { lazy, Suspense, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useCheckInternetConnection } from '@/hooks'
import { apiService } from '@/services/apiService'
import {
    isTestCashDepositEnabled,
    simulateTestDeposit
} from '@/utilities/testDepositHelper'

const ReceiptRendererDialog = lazy(() => import('./receiptRendererDialog'))

const Debug = () => {
    const navigate = useNavigate()
    const { deviceConfiguration } = useCheckInternetConnection()
    // Runtime debug mode state
    const [runtimeDebugMode, setRuntimeDebugMode] = useState(() => {
        return localStorage.getItem('runtime_debug_mode') === 'true'
    })

    // Hidden debug activation state
    const [clickCount, setClickCount] = useState(0)
    const [firstClickTime, setFirstClickTime] = useState<number | null>(null)

    // PIN entry state
    const [showPinEntry, setShowPinEntry] = useState(false)
    const [enteredDigits, setEnteredDigits] = useState<number[]>([])
    const [pinTimeout, setPinTimeout] = useState<number | null>(null)

    // Debug menu state
    const [isDebugMenuOpen, setIsDebugMenuOpen] = useState(false)
    const [isReceiptRendererOpen, setIsReceiptRendererOpen] = useState(false)

    // Speed test panel state
    const [isSpeedTestOpen, setIsSpeedTestOpen] = useState(false)

    // Peripheral diagnostics dashboard (device API) overlay
    const [isPeripheralDiagnosticsOpen, setIsPeripheralDiagnosticsOpen] =
        useState(false)

    const PERIPHERAL_DIAGNOSTICS_URL =
        'http://localhost:3001/files/test/index.html'

    // Check if debug mode is active (either build-time or runtime)
    const isDebugActive =
        import.meta.env.VITE_DEBUG_MODE === 'true' || runtimeDebugMode

    const clearPinTimeout = () => {
        if (pinTimeout) {
            clearTimeout(pinTimeout)
            setPinTimeout(null)
        }
    }

    const resetPinEntry = () => {
        setShowPinEntry(false)
        setEnteredDigits([])
        clearPinTimeout()
    }

    const startPinTimeout = () => {
        clearPinTimeout()
        const timeout = setTimeout(() => {
            resetPinEntry()
        }, 10000) // 10 seconds
        setPinTimeout(timeout as unknown as number)
    }

    const handleDigitClick = (digit: number) => {
        clearPinTimeout()
        const newDigits = [...enteredDigits, digit]
        setEnteredDigits(newDigits)

        // Check if we have the last 4 digits as 2213
        if (newDigits.length >= 4) {
            const lastFour = newDigits.slice(-4)
            if (lastFour.join('') === '2213') {
                // Activate debug mode
                setRuntimeDebugMode(true)
                localStorage.setItem('runtime_debug_mode', 'true')
                resetPinEntry()
                alert('Debug mode activated!')
                return
            }
        }

        // Restart timeout
        startPinTimeout()
    }

    const handleSecretAreaClick = () => {
        const now = Date.now()

        // Reset if more than 5 seconds have passed since first click
        if (firstClickTime && now - firstClickTime > 5000) {
            setClickCount(1)
            setFirstClickTime(now)
            return
        }

        // First click
        if (clickCount === 0) {
            setClickCount(1)
            setFirstClickTime(now)
        } else {
            const newCount = clickCount + 1
            setClickCount(newCount)

            // Check if we've reached 10 clicks
            if (newCount >= 10) {
                // Reset click tracking
                setClickCount(0)
                setFirstClickTime(null)

                // Show PIN entry interface
                setShowPinEntry(true)
                setEnteredDigits([])
                startPinTimeout()
            }
        }
    }

    const handleDisableDebugMode = () => {
        setRuntimeDebugMode(false)
        localStorage.removeItem('runtime_debug_mode')
        alert('Debug mode disabled')
    }

    const handleMoneyTransferVoucherDraft = async () => {
        try {
            await simulateTestDeposit()
            console.log('[debug] Simulated test deposit created')
        } catch (error) {
            console.error('[debug] Simulated test deposit failed', error)
        }
    }

    const handlePrintDebug = async () => {
        alert('About to call apiService.print() with template URL...')

        const voucherData = {
            url: 'https://vcash.rs/urlmorabitimaloduzidabipreci/dstaviokakoceizgledati/kadasezapravokoristizanestokorisnostousebiukljucujekodvaucereaidrugekorisnestvari',
            voucherCode: '123-456-789',
            publicCode: 'APT-APT',
            venueName: 'Test Uplatno Mesto  ',
            venueAddress: 'Булевар Вудроа Вилсона 14',
            venueCity: 'Београд',
            amount: '1.000',
            currencyCode: 'RSD',
            createdAt: '04.10.2023. 23:28',
            currentTime: '04.10.2022. 23:28',
            voucherType: 'Bet Vaučer'
        }

        const jsonString = JSON.stringify(voucherData)
        const utf8Bytes = new TextEncoder().encode(jsonString)

        // Convert Uint8Array to base64
        const base64Data = btoa(
            Array.from(utf8Bytes, (byte) => String.fromCharCode(byte)).join('')
        )

        const templateUrl = `https://bp-templates.fly.dev/render/terminal_voucher?data=${encodeURIComponent(base64Data)}&type=bmp&rotate=180`

        try {
            const response = await apiService.print(templateUrl)
            alert(`Print response: ${JSON.stringify(response, null, 2)}`)
        } catch (error) {
            alert(
                `Print error: ${error instanceof Error ? error.message : 'Unknown error'}`
            )
        }
    }

    // const handleGetCredentialsDebug = async () => {
    //     alert('About to call apiService.getCredentials()...')

    //     try {
    //         const response = await apiService.getCredentials()
    //         alert(`Credentials response: ${JSON.stringify(response, null, 2)}`)
    //     } catch (error) {
    //         alert(
    //             `Credentials error: ${error instanceof Error ? error.message : 'Unknown error'}`
    //         )
    //     }
    // }

    // const handleResetDeviceRegistration = async () => {
    //     const confirmed = confirm(
    //         'Are you sure you want to reset device registration? This will clear all tokens and require re-registration.'
    //     )

    //     if (!confirmed) return

    //     alert('Resetting device registration...')

    //     try {
    //         // Try clearing with "token-reset" to ensure it overwrites
    //         const resetTokenResponse =
    //             await apiService.saveDeviceToken('token-reset')

    //         // Show the API response
    //         alert(
    //             `Reset Token API Response: ${JSON.stringify(resetTokenResponse, null, 2)}`
    //         )

    //         // Also try with empty string
    //         const emptyTokenResponse = await apiService.saveDeviceToken('')

    //         // Show the empty token API response
    //         alert(
    //             `Empty Token API Response: ${JSON.stringify(emptyTokenResponse, null, 2)}`
    //         )

    //         // Clear localStorage tokens
    //         AuthService.DeleteToken(Auth.POS)
    //         AuthService.DeleteToken(Auth.Cashier)

    //         // Verify the token was actually cleared by checking what's stored
    //         const verifyToken = await apiService.getDeviceToken()

    //         alert(
    //             `Device registration reset completed!\nStored token after reset: "${verifyToken}"\nPlease refresh the page.`
    //         )
    //     } catch (error) {
    //         alert(
    //             `Reset error: ${error instanceof Error ? error.message : 'Unknown error'}`
    //         )
    //     }
    // }

    const handleRefreshPage = () => {
        window.location.reload()
    }

    const handleDemo = () => {
        window.location.assign('https://terminal-app-git-a1demo-vcash.vercel.app/')
    }

    /**
     * Last-ditch recovery: wipe localStorage so the kiosk re-bootstraps from
     * scratch.
     *
     * The app deliberately never clears the device token on its own (it would
     * lock out a registered device whose local-API token file is temporarily
     * missing). This is the manual escape hatch when the local API has truly
     * lost the token for good - clearing localStorage and reloading sends the
     * kiosk back to the registration flow so support can re-register manually.
     *
     * Two confirmations because this is irreversible from the kiosk's side
     * (the device must be re-registered from the cloud after this).
     */
    const handleClearLocalStorage = () => {
        const confirmed = confirm(
            'CLEAR LOCAL STORAGE?\n\n' +
                'This wipes ALL kiosk-side state, including the device token.\n\n' +
                'Use ONLY when the device token is truly lost and the kiosk needs ' +
                'to be manually re-registered. The page will reload afterwards.\n\n' +
                'Continue?'
        )
        if (!confirmed) return

        const doubleConfirmed = confirm(
            'Are you absolutely sure?\n\n' +
                'After this, this device will need to be re-registered before it ' +
                'can be used again.'
        )
        if (!doubleConfirmed) return

        try {
            localStorage.clear()
            console.warn(
                '[debug] localStorage cleared manually via debug menu - kiosk will reload to re-bootstrap'
            )
        } catch (error) {
            alert(
                `Failed to clear localStorage: ${error instanceof Error ? error.message : 'Unknown error'}`
            )
            return
        }

        window.location.reload()
    }

    // const handleNavigateToAltUrl = () => {
    //     const altUrl = import.meta.env.VITE_ALT_URL

    //     if (!altUrl || typeof altUrl !== 'string' || altUrl.trim() === '') {
    //         alert('VITE_ALT_URL is not set or is empty')
    //         return
    //     }

    //     if (!altUrl.startsWith('http://') && !altUrl.startsWith('https://')) {
    //         alert('VITE_ALT_URL must begin with http:// or https://')
    //         return
    //     }

    //     const confirmed = confirm(`Navigate to: ${altUrl}?`)
    //     if (confirmed) {
    //         window.location.href = altUrl
    //     }
    // }

    const handlePreviewDepositAnimation = () => {
        handleCloseDebugMenu()
        navigate('/debug/deposit-animation')
    }

    const handleOpenSpeedTest = () => {
        setIsSpeedTestOpen(true)
    }

    const handleOpenPeripheralDiagnostics = () => {
        setIsDebugMenuOpen(false)
        setIsSpeedTestOpen(false)
        setIsPeripheralDiagnosticsOpen(true)
    }

    const handleClosePeripheralDiagnostics = () => {
        setIsPeripheralDiagnosticsOpen(false)
    }

    const handleCloseDebugMenu = () => {
        setIsDebugMenuOpen(false)
        setIsSpeedTestOpen(false)
    }

    return (
        <>
            {/* Hidden debug activation area - only render when debug is not active */}
            {!isDebugActive && (
                <div
                    onClick={handleSecretAreaClick}
                    style={{
                        position: 'absolute',
                        top: 0,
                        right: '350px', // Position just before language buttons
                        width: '320px',
                        height: '80px',
                        cursor: 'default',
                        zIndex: 1000
                        // Uncomment next line for debugging the click area
                        // backgroundColor: 'rgba(255, 0, 0, 0.1)'
                    }}
                    title="" // No tooltip to keep it hidden
                />
            )}

            {/* PIN Entry Interface */}
            {showPinEntry && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100px',
                        right: '50px',
                        background: 'rgba(0, 0, 0, 0.9)',
                        padding: '20px',
                        borderRadius: '8px',
                        zIndex: 2000
                    }}>
                    <div
                        style={{
                            color: 'white',
                            marginBottom: '15px',
                            textAlign: 'center',
                            fontSize: '14px'
                        }}>
                        Enter PIN (last 4 digits:{' '}
                        {enteredDigits.slice(-4).join('')})
                    </div>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(5, 1fr)',
                            gap: '8px',
                            maxWidth: '250px'
                        }}>
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                            <button
                                key={digit}
                                onClick={() => handleDigitClick(digit)}
                                style={{
                                    padding: '12px',
                                    background: '#3498db',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    minWidth: '40px'
                                }}>
                                {digit}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={resetPinEntry}
                        style={{
                            marginTop: '15px',
                            padding: '8px 12px',
                            background: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            width: '100%'
                        }}>
                        Cancel
                    </button>
                </div>
            )}

            {/* Debug menu */}
            {isDebugActive && (
                <>
                    <span style={{ color: 'white' }}>
                        build: {__BUILD_TIMESTAMP__}
                    </span>
                    <button
                        onClick={() => setIsDebugMenuOpen(true)}
                        style={{
                            marginLeft: '10px',
                            padding: '5px 10px',
                            background: '#2c3e50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                        }}>
                        Debug Menu
                    </button>
                </>
            )}

            {/* Debug Menu Dialog */}
            {isDebugMenuOpen && (
                <dialog
                    open
                    style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90vw',
                        height: '90vh',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '20px',
                        backgroundColor: 'white',
                        zIndex: 10000
                    }}
                    onClick={(e) => {
                        // Close if clicking on backdrop
                        if (e.target === e.currentTarget) {
                            handleCloseDebugMenu()
                        }
                    }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '20px'
                        }}>
                        <h3 style={{ margin: 0 }}>Debug Menu</h3>
                        <button
                            onClick={handleCloseDebugMenu}
                            style={{
                                background: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}>
                            Close
                        </button>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '10px',
                            marginBottom: '20px'
                        }}>
                        {runtimeDebugMode && (
                            <button
                                onClick={handleDisableDebugMode}
                                style={{
                                    padding: '10px 14px',
                                    background: '#95a5a6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}>
                                Disable Debug
                            </button>
                        )}
                        <button
                            onClick={handlePrintDebug}
                            style={{
                                padding: '10px 14px',
                                background: '#ff6b6b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}>
                            Debug Print
                        </button>
                        <button
                            onClick={() => setIsReceiptRendererOpen(true)}
                            style={{
                                padding: '10px 14px',
                                background: '#385ac9',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}>
                            Test template renderer
                        </button>
                        <button
                            onClick={handleRefreshPage}
                            style={{
                                padding: '10px 14px',
                                background: '#9b59b6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}>
                            Refresh
                        </button>
                        <button
                            onClick={handleDemo}
                            style={{
                                padding: '10px 14px',
                                background: '#385ac9',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}>
                            demo
                        </button>
                        <button
                            onClick={handleOpenSpeedTest}
                            style={{
                                padding: '10px 14px',
                                background: '#3498db',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}>
                            Speed Test
                        </button>
                        <button
                            onClick={handlePreviewDepositAnimation}
                            style={{
                                padding: '10px 14px',
                                background: '#385ac9',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}>
                            Deposit animation (500 + 200)
                        </button>
                        <button
                            onClick={handleOpenPeripheralDiagnostics}
                            style={{
                                padding: '10px 14px',
                                background: '#16a085',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}>
                            Peripheral Diagnostics
                        </button>
                        {(import.meta.env.VITE_DEBUG_MODE === 'true' ||
                            isTestCashDepositEnabled()) && (
                            <button
                                onClick={handleMoneyTransferVoucherDraft}
                                style={{
                                    padding: '10px 14px',
                                    background: '#27ae60',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}>
                                Simulate Deposit (500)
                            </button>
                        )}
                        <button
                            onClick={handleClearLocalStorage}
                            title="Last-ditch: wipe kiosk-side state (incl. device token) and force re-registration"
                            style={{
                                padding: '10px 14px',
                                background: '#7f1d1d',
                                color: 'white',
                                border: '2px solid #fca5a5',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                marginLeft: 'auto'
                            }}>
                            Clear localStorage (re-register)
                        </button>
                    </div>

                    {isSpeedTestOpen && (
                        <section
                            style={{
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                marginBottom: '20px',
                                padding: '12px'
                            }}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px'
                                }}>
                                <h4 style={{ margin: 0 }}>Internet Speed Test</h4>
                                <button
                                    onClick={() => setIsSpeedTestOpen(false)}
                                    style={{
                                        background: '#e74c3c',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '6px 10px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                    }}>
                                    Hide
                                </button>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ minHeight: '360px' }}>
                                    <div
                                        style={{
                                            width: '100%',
                                            height: 0,
                                            paddingBottom: '50%',
                                            position: 'relative'
                                        }}>
                                        <iframe
                                            style={{
                                                border: 'none',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                minHeight: '360px',
                                                overflow: 'hidden !important'
                                            }}
                                            src="https://www.metercustom.net/plugin/"
                                        />
                                    </div>
                                </div>
                                Provided by{' '}
                                <a
                                    href="https://www.meter.net"
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    Meter.net
                                </a>
                            </div>
                        </section>
                    )}

                    <div>
                        <h4 style={{ marginBottom: '10px' }}>
                            Device Configuration
                        </h4>
                        <pre
                            style={{
                                background: '#f4f6f8',
                                border: '1px solid #dce1e6',
                                borderRadius: '6px',
                                color: '#1f2933',
                                fontSize: '13px',
                                lineHeight: 1.4,
                                margin: 0,
                                maxHeight: isSpeedTestOpen ? '22vh' : '58vh',
                                overflow: 'auto',
                                padding: '12px',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}>
                            {deviceConfiguration
                                ? JSON.stringify(deviceConfiguration, null, 2)
                                : 'No device configuration loaded yet.'}
                        </pre>
                    </div>
                </dialog>
            )}

            {/* Peripheral diagnostics dashboard overlay (device API) */}
            {isReceiptRendererOpen && (
                <Suspense fallback={<div role="status" style={{ position: 'fixed', zIndex: 20001, top: '25%', left: '25%', padding: 24, background: 'white', color: '#1f2933' }}>Loading receipt renderer…</div>}>
                    <ReceiptRendererDialog onClose={() => setIsReceiptRendererOpen(false)} />
                </Suspense>
            )}
            {isPeripheralDiagnosticsOpen && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 20000,
                        background: '#000',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '10px 16px',
                            background: '#1f2933',
                            flexShrink: 0
                        }}>
                        <span style={{ color: 'white', fontSize: '14px' }}>
                            Peripheral Diagnostics
                        </span>
                        <button
                            onClick={handleClosePeripheralDiagnostics}
                            style={{
                                background: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '10px 16px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}>
                            Close
                        </button>
                    </div>
                    <iframe
                        title="Peripheral Diagnostics"
                        src={PERIPHERAL_DIAGNOSTICS_URL}
                        style={{
                            flex: 1,
                            width: '100%',
                            border: 'none',
                            background: 'white'
                        }}
                    />
                </div>
            )}
        </>
    )
}

export default Debug
